import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { History, Smartphone, Laptop, MapPin, RefreshCw, Search, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || "-"; } };
const isMobile = (d) => /HP|Tablet|Android|iPhone|iPad/i.test(d || "");

export default function AdminLoginHistory() {
  const [rows, setRows] = useState([]);
  const [maxDevices, setMaxDevices] = useState(2);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/login-sessions");
      setRows(r.data?.sessions || []);
      setMaxDevices(r.data?.max_devices || 2);
    } catch { toast.error("Gagal memuat riwayat login (admin)"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => [r.email, r.name, r.device, r.country, r.city, r.ip].filter(Boolean).join(" ").toLowerCase().includes(s));
  }, [rows, q]);

  const uniqueUsers = useMemo(() => new Set(rows.map((r) => r.user_id)).size, [rows]);
  const activeTotal = useMemo(() => rows.filter((r) => r.active).length, [rows]);

  return (
    <div className="p-4 sm:p-8 max-w-[1100px] mx-auto" data-testid="admin-login-history-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-sky-400/90 flex items-center gap-2"><History className="h-4 w-4" /> Admin · Keamanan</div>
          <h1 className="font-head font-black text-4xl uppercase tracking-tight mt-1">Riwayat Login User</h1>
          <p className="text-slate-500 text-sm mt-1">Lokasi, negara & perangkat login semua user. Batas <b className="text-sky-400">{maxDevices} perangkat</b> aktif per akun.</p>
        </div>
        <button data-testid="admin-login-history-refresh" onClick={load} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-700 text-slate-200 hover:bg-slate-800 text-sm font-semibold">
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="inline-flex items-center gap-2 text-sm text-slate-300 bg-[#0c1017] border border-slate-700 rounded-lg px-3 py-2"><Users className="h-4 w-4 text-sky-400" /> User: <b className="text-sky-400">{uniqueUsers}</b></div>
        <div className="inline-flex items-center gap-2 text-sm text-slate-300 bg-[#0c1017] border border-slate-700 rounded-lg px-3 py-2">Sesi aktif: <b className="text-emerald-400">{activeTotal}</b></div>
        <div className="inline-flex items-center gap-2 text-sm text-slate-300 bg-[#0c1017] border border-slate-700 rounded-lg px-3 py-2">Total tercatat: <b className="text-slate-200">{rows.length}</b></div>
        <div className="flex-1 min-w-[220px] relative">
          <Search className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="admin-login-history-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari email / perangkat / negara / IP…"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#0c1017] border border-slate-700 text-slate-200 text-sm focus:border-sky-500 outline-none" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 overflow-hidden">
        <div className="hidden md:grid grid-cols-[1.4fr_1.4fr_1.4fr_1fr_0.8fr] gap-2 px-4 py-2 bg-[#0c1017] text-[11px] uppercase tracking-wider text-slate-500 font-bold">
          <div>User</div><div>Perangkat</div><div>Lokasi</div><div>Waktu</div><div>Status</div>
        </div>
        {filtered.length === 0 && !busy && <div className="px-4 py-6 text-slate-500 text-sm" data-testid="admin-login-history-empty">Tidak ada data.</div>}
        {filtered.map((s, i) => (
          <div key={s.sid || i} data-testid={`admin-login-history-row-${i}`} onClick={() => setDetail(s)} title="Klik untuk lihat detail"
            className={`grid grid-cols-1 md:grid-cols-[1.4fr_1.4fr_1.4fr_1fr_0.8fr] gap-1 md:gap-2 px-4 py-3 border-t border-slate-800 text-sm cursor-pointer hover:bg-sky-500/5 ${s.active ? "bg-[#0a0e16]" : "bg-transparent opacity-70"}`}>
            <div className="min-w-0">
              <div className="text-slate-100 font-semibold truncate">{s.email || s.user_id}</div>
              {s.name && <div className="text-xs text-slate-500 truncate">{s.name}</div>}
            </div>
            <div className="min-w-0 flex items-center gap-2 text-slate-300">
              <span className="shrink-0 text-slate-500">{isMobile(s.device) ? <Smartphone className="h-4 w-4" /> : <Laptop className="h-4 w-4" />}</span>
              <span className="truncate">{s.device}{s.browser ? ` · ${s.browser}` : ""}</span>
            </div>
            <div className="min-w-0 text-slate-400 flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 text-slate-500 shrink-0" />
              <span className="truncate">{[s.city, s.region, s.country].filter(Boolean).join(", ") || "—"}{s.country_code ? ` (${s.country_code})` : ""}{s.ip ? ` · ${s.ip}` : ""}</span>
            </div>
            <div className="text-slate-400 text-xs">{fmt(s.created_at)}</div>
            <div>
              {s.active
                ? <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300">Aktif</span>
                : <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-slate-700 text-slate-300">Keluar{s.ended_reason === "device_limit" ? " (batas)" : ""}</span>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#0c1017] border-slate-700 text-slate-200 max-w-lg" data-testid="admin-login-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detail && (isMobile(detail.device) ? <Smartphone className="h-5 w-5 text-sky-400" /> : <Laptop className="h-5 w-5 text-sky-400" />)}
              Detail Sesi Login
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              {[
                ["User", detail.email || detail.user_id],
                ["Nama", detail.name || "-"],
                ["Perangkat", detail.device || "-"],
                ["Browser", detail.browser || "-"],
                ["OS", detail.os || "-"],
                ["Negara", `${detail.country || "-"}${detail.country_code ? ` (${detail.country_code})` : ""}`],
                ["Kota / Wilayah", [detail.city, detail.region].filter(Boolean).join(", ") || "-"],
                ["Alamat IP", detail.ip || "-"],
                ["Waktu Login", fmt(detail.created_at)],
                ["Aktivitas Terakhir", fmt(detail.last_seen)],
                ["Status", detail.active ? "Aktif" : `Keluar${detail.ended_reason === "device_limit" ? " (batas perangkat)" : detail.ended_reason ? ` (${detail.ended_reason})` : ""}`],
                ["Session ID", detail.sid || "-"],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-[130px_1fr] gap-2 border-b border-slate-800/70 pb-1.5">
                  <div className="text-slate-500">{k}</div>
                  <div className="text-slate-100 break-words font-mono-tt text-[13px]">{v}</div>
                </div>
              ))}
              <div>
                <div className="text-slate-500 text-xs mb-1">User-Agent lengkap</div>
                <div className="text-[11px] text-slate-400 bg-[#0a0e16] border border-slate-800 rounded p-2 break-all font-mono-tt" data-testid="admin-login-detail-ua">{detail.user_agent || "-"}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
